import React, { useState } from 'react';
import { Database, AlertTriangle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { seedDatabase, checkHealth } from '../services/api';

interface SeedStats {
  deleted: {
    projects: number;
    factSheets: number;
    analyses: number;
  };
  created: {
    projects: number;
    factSheets: number;
    analyses: number;
  };
}

const SettingsPage: React.FC = () => {
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{
    success: boolean;
    message: string;
    stats?: SeedStats;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [healthStatus, setHealthStatus] = useState<{
    status: string;
    database: { connected: boolean };
    databaseError?: string;
  } | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);

  const handleCheckHealth = async () => {
    setIsCheckingHealth(true);
    try {
      const health = await checkHealth();
      setHealthStatus(health as any);
    } catch (error) {
      setHealthStatus({
        status: 'error',
        database: { connected: false },
        databaseError: error instanceof Error ? error.message : 'Failed to check health',
      });
    } finally {
      setIsCheckingHealth(false);
    }
  };

  const handleSeedClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSeed = async () => {
    setShowConfirmDialog(false);
    setIsSeeding(true);
    setSeedResult(null);

    try {
      const result = await seedDatabase();
      setSeedResult({
        success: result.success,
        message: result.message || 'Database seeded successfully',
        stats: result.stats,
      });
    } catch (error) {
      setSeedResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to seed database',
      });
    } finally {
      setIsSeeding(false);
    }
  };

  const handleCancelSeed = () => {
    setShowConfirmDialog(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      {/* Database Health Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Database Status</h2>
          </div>
          <button
            onClick={handleCheckHealth}
            disabled={isCheckingHealth}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-2"
          >
            {isCheckingHealth ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Check Status
          </button>
        </div>

        {healthStatus && (
          <div className={`p-4 rounded-lg ${
            healthStatus.database.connected
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {healthStatus.database.connected ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Database Connected</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 font-medium">Database Disconnected</span>
                </>
              )}
            </div>
            {healthStatus.databaseError && (
              <p className="mt-2 text-sm text-red-600">{healthStatus.databaseError}</p>
            )}
            <p className="mt-2 text-sm text-gray-600">
              Status: {healthStatus.status}
            </p>
          </div>
        )}
      </div>

      {/* Database Seeding Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Database className="w-6 h-6 text-orange-600" />
          <h2 className="text-lg font-semibold text-gray-900">Database Seeding</h2>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-yellow-800 font-medium">Warning: Destructive Operation</p>
              <p className="text-yellow-700 text-sm mt-1">
                This will delete ALL existing data (projects, fact sheets, analyses) and replace it with sample data.
                This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <p className="text-gray-600 mb-4">
          Use this feature to reset the database with sample project data for testing and demonstration purposes.
          The seed data includes various projects with different statuses (clean, stage 1, stage 2) and sample fact sheet analyses.
        </p>

        <button
          onClick={handleSeedClick}
          disabled={isSeeding}
          className="px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSeeding ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Seeding Database...
            </>
          ) : (
            <>
              <Database className="w-5 h-5" />
              Reset & Seed Database
            </>
          )}
        </button>

        {seedResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            seedResult.success
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {seedResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              )}
              <span className={seedResult.success ? 'text-green-800' : 'text-red-800'}>
                {seedResult.message}
              </span>
            </div>

            {seedResult.stats && (
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Deleted:</p>
                  <ul className="text-sm text-gray-600 mt-1">
                    <li>{seedResult.stats.deleted.projects} projects</li>
                    <li>{seedResult.stats.deleted.factSheets} fact sheets</li>
                    <li>{seedResult.stats.deleted.analyses} analyses</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Created:</p>
                  <ul className="text-sm text-gray-600 mt-1">
                    <li>{seedResult.stats.created.projects} projects</li>
                    <li>{seedResult.stats.created.factSheets} fact sheets</li>
                    <li>{seedResult.stats.created.analyses} analyses</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Database Reset</h3>
                <p className="text-sm text-gray-500">This action is irreversible</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete all existing data and seed the database with sample data?
              This will permanently remove all projects, fact sheets, and analyses.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelSeed}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSeed}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Yes, Reset Database
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
